import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Download } from 'lucide-react';
import { Section, SettingRow } from '@vnetwork/ui/layout';
import { Button, Badge, Switch, Input } from '@vnetwork/ui/primitives';
import { Alert, ConfirmDialog, useToast } from '@vnetwork/ui/feedback';
import { useDeleteCluster, fetchKubeconfig, type K8sCluster } from '@/api/k8s';

// kubeconfig certificate lifetime, in days. Backend accepts 1–365; the console
// caps it at 90 so issued credentials rotate at least quarterly.
const KUBECONFIG_MIN_DAYS = 1;
const KUBECONFIG_MAX_DAYS = 90;
const KUBECONFIG_DEFAULT_DAYS = 90;

// Alert types are a UI-only preview — no backend wiring yet, so this is a flat
// const, not data. Each row drives a local toggle; nothing here persists or fires.
const ALERT_TYPES = [
  { id: 'node-notready', label: 'Node NotReady', description: 'A worker node stops reporting as Ready.' },
  { id: 'control-plane', label: 'Control plane unreachable', description: "The cluster API / control plane can't be reached." },
  { id: 'cpu-high', label: 'High CPU usage', description: 'Sustained high CPU across the cluster.' },
  { id: 'mem-high', label: 'High memory usage', description: 'Sustained high memory pressure across the cluster.' },
  { id: 'disk-pressure', label: 'Disk pressure', description: 'A node reports low available disk.' },
] as const;

export function ClusterSettings({ serviceId, clusterId, cluster }: { serviceId: string; clusterId: string; cluster: K8sCluster }) {
  const navigate = useNavigate();
  const { show } = useToast();
  const listPath = `/services/kubernetes/${serviceId}/clusters`;

  // Local-only preview state for the Alerts (beta) section. Intentionally not a
  // query/mutation — the feature isn't wired to a backend yet.
  const [enabled, setEnabled] = useState<Set<string>>(new Set());
  const [email, setEmail] = useState('');
  const toggle = (id: string) =>
    setEnabled((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });

  // kubeconfig lifetime is a per-generation parameter (not a stored cluster
  // setting): the chosen value is sent as `expires_days` when downloading.
  const [expiresDays, setExpiresDays] = useState(String(KUBECONFIG_DEFAULT_DAYS));
  const [generating, setGenerating] = useState(false);
  const days = Number(expiresDays);
  const daysValid = Number.isInteger(days) && days >= KUBECONFIG_MIN_DAYS && days <= KUBECONFIG_MAX_DAYS;
  // Backend refuses to issue credentials for a cluster that isn't active.
  const clusterReady = /active|running|ready/i.test(cluster.status ?? cluster.state ?? '');

  const downloadKubeconfig = async () => {
    if (!daysValid || !clusterReady) return;
    setGenerating(true);
    try {
      const yamlText = await fetchKubeconfig(serviceId, clusterId, days);
      const blob = new Blob([yamlText], { type: 'text/yaml' });
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = `${cluster.name || 'cluster'}-kubeconfig.yaml`;
      a.click();
      URL.revokeObjectURL(a.href);
      show({ tone: 'success', title: 'kubeconfig downloaded', description: `Valid for ${days} day${days === 1 ? '' : 's'}.` });
    } catch {
      show({ tone: 'danger', title: 'Could not generate kubeconfig' });
    } finally {
      setGenerating(false);
    }
  };

  const [confirmDelete, setConfirmDelete] = useState(false);
  const remove = useDeleteCluster(serviceId, {
    onSuccess: () => { show({ tone: 'success', title: 'Cluster deleted' }); navigate(listPath); },
    onError: () => show({ tone: 'danger', title: 'Delete failed' }),
  });

  return (
    <div style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>
      {/* Alerts (beta) — preview only. Controls are live so the layout feels real
          and persistence can drop in later, but the commit action is disabled and
          a banner states plainly that nothing is saved or sent yet. */}
      <Section
        title="Alerts"
        description="Get notified when your cluster health changes. Preview only — these settings don't save or send notifications yet."
        actions={<Badge tone="accent" appearance="subtle" size="sm">Beta</Badge>}
        style={{ marginBottom: 0 }}
      >
        <Alert tone="info" title="Coming soon" style={{ marginBottom: 'var(--vn-space-4)' }}>
          Alerting isn't wired up yet. You can preview the controls here, but nothing is saved or triggered.
        </Alert>

        <div style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>
          {ALERT_TYPES.map((a) => (
            <SettingRow
              key={a.id}
              title={a.label}
              description={a.description}
              control={<Switch checked={enabled.has(a.id)} onCheckedChange={() => toggle(a.id)} />}
            />
          ))}

          <SettingRow
            title="Notification email"
            description="Where alert emails would be sent."
            control={
              <Input
                type="email"
                aria-label="Notification email"
                placeholder="you@company.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                inputSize="sm"
                style={{ minWidth: 'min(220px, 100%)', maxWidth: '100%' }}
              />
            }
            footer={<Button size="sm" disabled>Save (coming soon)</Button>}
          />
        </div>
      </Section>

      {/* kubeconfig — pick a certificate lifetime (1–90 days) and download a fresh
          kubeconfig generated with that expiry (sent as `expires_days`). */}
      <Section title="kubeconfig" description="Download a fresh kubeconfig for this cluster. Choose how long its client certificate stays valid." style={{ marginBottom: 0 }}>
        <SettingRow
          title="Certificate lifetime"
          description={`How long the downloaded kubeconfig stays valid, in days (${KUBECONFIG_MIN_DAYS}–${KUBECONFIG_MAX_DAYS}). Defaults to ${KUBECONFIG_DEFAULT_DAYS}.`}
          control={
            <Input
              type="number"
              aria-label="kubeconfig certificate lifetime in days"
              min={KUBECONFIG_MIN_DAYS}
              max={KUBECONFIG_MAX_DAYS}
              step={1}
              value={expiresDays}
              onChange={(e) => setExpiresDays(e.target.value)}
              invalid={!daysValid}
              inputSize="sm"
              endSlot={<span style={{ color: 'var(--vn-fg-muted)', fontSize: 'var(--vn-font-size-sm)' }}>days</span>}
              style={{ width: 132 }}
            />
          }
          footer={
            <Button
              size="sm"
              startIcon={<Download size={15} />}
              disabled={!daysValid || !clusterReady || generating}
              onClick={downloadKubeconfig}
            >
              {generating ? 'Generating…' : 'Download kubeconfig'}
            </Button>
          }
        >
          {!clusterReady && (
            <span style={{ color: 'var(--vn-fg-muted)', fontSize: 'var(--vn-font-size-sm)' }}>
              Available once the cluster is active.
            </span>
          )}
        </SettingRow>
      </Section>

      {/* Danger zone — mirrors the project danger-zone pattern: a destructive
          action gated by a typed-name confirmation. */}
      <Section title="Danger zone" description="Destructive actions for this cluster." style={{ marginBottom: 0 }}>
        <SettingRow
          title="Delete this cluster"
          description="Permanently delete this cluster and all its workloads. This action cannot be undone."
          control={<Button variant="danger" size="sm" onClick={() => setConfirmDelete(true)}>Delete cluster</Button>}
        />
      </Section>

      <ConfirmDialog
        open={confirmDelete}
        onOpenChange={(o) => !o && setConfirmDelete(false)}
        title="Delete cluster?"
        description={`Permanently delete “${cluster.name || clusterId}” and all its workloads?`}
        confirmLabel="Delete"
        destructive
        requireText={cluster.name || clusterId}
        loading={remove.isPending}
        onConfirm={() => remove.mutate(clusterId)}
      />
    </div>
  );
}
